"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeAddressStreetDisplay = makeAddressStreetDisplay;
exports.makeLocationPointString = makeLocationPointString;
exports.makeAddressCityStateString = makeAddressCityStateString;
exports.makeFullAddressDisplay = makeFullAddressDisplay;
exports.removeSpecialCharacters = removeSpecialCharacters;
exports.normalizeAddress = normalizeAddress;
exports.extractStreetNumber = extractStreetNumber;
exports.extractStreetUnitNumber = extractStreetUnitNumber;
exports.extractZipCode = extractZipCode;
exports.extractStreetOrientation = extractStreetOrientation;
exports.normalizeUnitNumber = normalizeUnitNumber;
const utility_1 = require("@utilityprofit/utility");
const constants_1 = require("./constants");
const unwantedTokensRegex = /unit|apartment|apt|suite/gi;
const unitKeywords = ['apt', 'unit'];
function makeAddressStreetDisplay(address = {}) {
    if (!(address === null || address === void 0 ? void 0 : address.streetName)) {
        return null;
    }
    const addressStreet = [
        address.streetNumber || '',
        address.streetPrefix || '',
        address.streetName || '',
        address.streetSuffix || '',
        address.unitNumber || '',
    ]
        .map((value) => value.trim())
        .filter(Boolean)
        .join(' ');
    return addressStreet;
}
function makeLocationPointString(location) {
    const locationPointIsValid = typeof location.longitude === 'number' &&
        !isNaN(location.longitude) &&
        typeof location.latitude === 'number' &&
        !isNaN(location.latitude);
    const listingLocationPointString = locationPointIsValid
        ? `Point(${location.longitude} ${location.latitude})`
        : null;
    return listingLocationPointString;
}
function makeAddressCityStateString(address = {}) {
    return [
        (address.city || '').trim(),
        [address.stateCode || '', address.zip || '']
            .map((value) => value.trim())
            .filter(Boolean)
            .join(' ')
            .trim(),
    ]
        .filter(Boolean)
        .join(', ');
}
function makeFullAddressDisplay(address) {
    const fullAddressString = [
        makeAddressStreetDisplay(address),
        makeAddressCityStateString(address),
    ]
        .filter(Boolean)
        .join(', ');
    return fullAddressString || null;
}
function removeSpecialCharacters(addressLine) {
    if (typeof addressLine !== 'string') {
        return '';
    }
    return addressLine.replace(/(?!\.5)[^A-Za-z\d\s]/g, '');
}
function normalizeAddress(address) {
    if (!address) {
        return null;
    }
    const normalizedAddress = Object.assign(Object.assign({}, address), { streetName: (0, utility_1.capitalizeAll)(address.streetName || '').replace(unwantedTokensRegex, '') || null, streetPrefix: (address.streetPrefix || '').toUpperCase() || null, streetSuffix: (0, utility_1.capitalizeAll)(address.streetSuffix || '') || null, city: (0, utility_1.capitalizeAll)(address.city || '') || null, county: (0, utility_1.capitalizeAll)(address.county || '') || null, countryCode: (address.countryCode || 'US').toUpperCase() || null, stateCode: (address.stateCode || '').toUpperCase() || null, streetNumber: address.streetNumber || null, unitNumber: address.unitNumber || null, zip: address.zip || null });
    ['streetName', 'streetPrefix', 'streetSuffix', 'city', 'unitNumber'].forEach((key) => {
        if (normalizedAddress[key] &&
            typeof normalizedAddress[key] === 'string') {
            normalizedAddress[key] = removeSpecialCharacters(normalizedAddress[key]);
        }
    });
    // Build a mapping of all orientation variants to their standard abbreviations
    const streetOrientationVariants = {};
    for (const [fullName, abbreviation] of Object.entries(constants_1.streetOrientationAbbreviations)) {
        streetOrientationVariants[fullName.toLowerCase()] =
            abbreviation.toUpperCase();
        streetOrientationVariants[abbreviation.toLowerCase()] =
            abbreviation.toUpperCase();
    }
    if (normalizedAddress.streetName) {
        if (!normalizedAddress.streetPrefix) {
            const streetParts = normalizedAddress.streetName.split(' ');
            const streetPrefixPart = streetParts.find((part) => streetOrientationVariants[part.toLowerCase()]);
            if (streetPrefixPart) {
                normalizedAddress.streetPrefix =
                    streetOrientationVariants[streetPrefixPart.toLowerCase()];
                normalizedAddress.streetName = streetParts
                    .filter((name) => name !== streetPrefixPart)
                    .join(' ')
                    .trim();
            }
        }
        else {
            const streetParts = normalizedAddress.streetName.split(' ');
            const prefixVariants = Object.keys(streetOrientationVariants).filter((key) => streetOrientationVariants[key] === normalizedAddress.streetPrefix);
            normalizedAddress.streetName = streetParts
                .filter((name) => !prefixVariants.includes(name.toLowerCase()))
                .join(' ')
                .trim();
        }
        if (normalizedAddress.unitNumber) {
            const lowercasedUnitNumber = normalizedAddress.unitNumber.toLowerCase();
            const streetParts = normalizedAddress.streetName.split(' ');
            const lowercasedLastStreetNamePart = streetParts[streetParts.length - 1].toLowerCase();
            if (lowercasedLastStreetNamePart === lowercasedUnitNumber) {
                normalizedAddress.streetName = streetParts
                    .slice(0, -1)
                    .join(' ')
                    .trim();
            }
        }
        if (!normalizedAddress.streetSuffix) {
            const streetParts = normalizedAddress.streetName.split(' ');
            const lastStreetPart = streetParts[streetParts.length - 1].toLowerCase();
            if (streetParts.length > 1 &&
                constants_1.allStreetSuffixes.includes(lastStreetPart)) {
                normalizedAddress.streetName = streetParts
                    .slice(0, -1)
                    .join(' ')
                    .trim();
                normalizedAddress.streetSuffix = (0, utility_1.capitalizeAll)(constants_1.streetSuffixMapping[lastStreetPart] || lastStreetPart);
            }
        }
        else {
            const lowercasedSuffix = normalizedAddress.streetSuffix.toLowerCase();
            const streetParts = normalizedAddress.streetName.split(' ');
            const lowercasedLastStreetNamePart = streetParts[streetParts.length - 1].toLowerCase();
            if (lowercasedLastStreetNamePart === lowercasedSuffix) {
                normalizedAddress.streetName = streetParts
                    .slice(0, -1)
                    .join(' ')
                    .trim();
            }
        }
    }
    if (normalizedAddress.streetSuffix) {
        const shortSuffix = constants_1.streetSuffixMapping[normalizedAddress.streetSuffix.toLowerCase()];
        if (shortSuffix) {
            normalizedAddress.streetSuffix = (0, utility_1.capitalizeAll)(shortSuffix);
        }
    }
    if (!normalizedAddress.streetName && normalizedAddress.streetPrefix) {
        normalizedAddress.streetName = normalizedAddress.streetPrefix.trim();
        normalizedAddress.streetPrefix = null;
    }
    normalizedAddress.streetDisplay = makeAddressStreetDisplay(normalizedAddress);
    normalizedAddress.display = makeFullAddressDisplay(normalizedAddress);
    return normalizedAddress;
}
function extractStreetNumber(streetName) {
    if (!streetName) {
        return null;
    }
    const streetNumber = streetName
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')[0]
        .replace(/[^0-9]/g, '');
    return streetNumber || null;
}
function extractStreetUnitNumber(streetName) {
    if (!streetName) {
        return null;
    }
    const streetNameLowercase = streetName.toLowerCase();
    for (const keyword of unitKeywords) {
        if (streetNameLowercase.includes(keyword)) {
            const keywordIndex = streetNameLowercase.indexOf(keyword);
            const unitNumber = streetName.slice(keywordIndex + keyword.length).trim();
            const unitNumberParts = unitNumber.split(' ');
            return normalizeUnitNumber(unitNumberParts[0]);
        }
    }
    const streetNameParts = streetName.trim().split(' ');
    let reverseStreetName = streetNameParts.length > 1
        ? removeSpecialCharacters(streetName
            .split(' ')
            .slice(1)
            .reverse()
            .filter((part) => {
            return !constants_1.streetOrientationAbbreviationsShortList.includes(part.toLowerCase());
        })
            .join(' '))
        : streetNameParts[0];
    const reverseStreetNameParts = reverseStreetName.split(' ');
    if (reverseStreetNameParts.length > 1 && reverseStreetNameParts[1]) {
        if ((!isNaN(Number(reverseStreetNameParts[0])) &&
            reverseStreetNameParts[0].length < 5 &&
            reverseStreetNameParts[1].match('[A-Za-z]') &&
            reverseStreetNameParts[1].length === 1) ||
            (reverseStreetNameParts[0].match('[A-Za-z]') &&
                reverseStreetNameParts[0].length === 1 &&
                !isNaN(Number(reverseStreetNameParts[1])) &&
                reverseStreetNameParts[1].length < 5)) {
            reverseStreetName = [
                `${reverseStreetNameParts[1]}${reverseStreetNameParts[0]}`,
                ...(reverseStreetNameParts.slice(2) || []),
            ].join(' ');
        }
    }
    // Regex to match ensuring at least one character or digit is present
    // This pattern attempts to match a word boundary, followed by any of:
    // - A single character
    // - 1 to 4 digits
    // - A character possibly preceded or followed by 1 to 4 digits
    // It ensures that there's at least one component present in the match
    const regex = /\b(?:([A-Za-z])|(\d{1,4})|([A-Za-z]?\d{1,4}[A-Za-z]?))\b/;
    const matches = reverseStreetName.match(regex);
    if (matches) {
        // Combine the groups to form the unit number. This accounts for any of the optional components being present.
        const unitNumber = matches[1] || matches[2] || matches[3];
        if (unitNumber) {
            return normalizeUnitNumber(unitNumber);
        }
    }
    return null;
}
function extractZipCode(zipCode) {
    if (!zipCode) {
        return null;
    }
    const cleanedZip = zipCode.replace(/[^0-9]/g, '').trim();
    if (cleanedZip.length < 5) {
        return null;
    }
    return cleanedZip.slice(0, 5);
}
function extractStreetOrientation(addressDisplay) {
    const addressParts = addressDisplay.toLowerCase().split(' ');
    const orientations = [
        ...Object.keys(constants_1.streetOrientationAbbreviations),
        ...Object.keys(constants_1.streetOrientationAbbreviationsReversed),
    ];
    for (const addressPart of addressParts) {
        if (orientations.includes(addressPart)) {
            return addressPart;
        }
    }
    // Check for full orientation names
    for (const [fullName] of Object.entries(constants_1.streetOrientationAbbreviations)) {
        if (addressDisplay.toLowerCase().includes(fullName.toLowerCase())) {
            return fullName.toLowerCase();
        }
    }
    return null;
}
function normalizeUnitNumber(unitNumber) {
    if (!unitNumber || typeof unitNumber !== 'string') {
        return null;
    }
    return removeSpecialCharacters(unitNumber)
        .replace(/[A-Za-z]{2,}/g, '')
        .replace(/\s/g, '')
        .toUpperCase()
        .trim();
}
//# sourceMappingURL=address-helpers.js.map